import { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from 'chart.js';
import { aiChatChartInkColors, aiChatChartSeriesColors } from 'constants/colors';

import { CHART_HEIGHT, CHART_SLICE_GAP } from '../constants/charts';

import type { ChartData, ChartOptions } from 'chart.js';
import type { ReactElement } from 'react';
import type IAiChatPieChart from '../interfaces/IAiChatPieChart';

import styles from '../sass/AiChat.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * A pie chart of a stored result: one slice per row, in the palette's fixed order, a ring of
 * surface between neighbours so two alike hues still part. The legend names the slices; the
 * tooltip gives each its value and its share.
 */
function AiChatPieChart({ data }: IAiChatPieChart): ReactElement {
  const total = useMemo(() => data.slices.reduce((sum, slice) => sum + slice.value, 0), [data]);

  const chartData: ChartData<'pie'> = useMemo(() => ({
    labels: data.slices.map((slice) => slice.label),
    datasets: [{
      data: data.slices.map((slice) => slice.value),
      backgroundColor: data.slices.map((_slice, index) => aiChatChartSeriesColors[index]),
      borderColor: aiChatChartInkColors.surface,
      borderWidth: CHART_SLICE_GAP,
      hoverBorderWidth: CHART_SLICE_GAP,
    }],
  }), [data]);

  const options: ChartOptions<'pie'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true, boxWidth: 8, padding: 12, color: aiChatChartInkColors.text,
        },
      },
      tooltip: {
        callbacks: {
          label: ({ label, parsed }) => {
            const share = total ? Math.round((parsed / total) * 100) : 0;

            return `${label}: ${parsed.toLocaleString()} (${share}%)`;
          },
        },
      },
    },
  }), [total]);

  return (
    <div className={styles.chart} style={{ height: CHART_HEIGHT }}>
      <Pie data={chartData} options={options} />
    </div>
  );
}

export default AiChatPieChart;
